import React, { useEffect, useState } from 'react'
import { Play, Award, CheckCircle } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useContentProgress } from '@/hooks/useContentProgress'
import { useRecentModules } from '@/hooks/useRecentModules'
import { supabase } from '@/integrations/supabase/client'

interface LearnContent {
  id: string
  title: string
  duration: string
  completed: boolean
  content_type: 'video' | 'reading' | 'quiz' | 'infographic'
  video_url?: string
  infographic_url?: string
  content_text?: string
  order_index: number
}

interface LessonPlayerProps {
  currentLesson: LearnContent | undefined
  moduleId: string | undefined
  moduleTitle?: string
  moduleDescription?: string
  moduleCategory?: string
  isContentCompleted: (contentId: string) => boolean
  markContentAsCompleted: (contentId: string) => Promise<any>
}

const LessonPlayer = ({ 
  currentLesson, 
  moduleId, 
  moduleTitle, 
  moduleDescription, 
  moduleCategory,
  isContentCompleted,
  markContentAsCompleted
}: LessonPlayerProps) => {
  const { addRecentModule } = useRecentModules()
  const [signedUrl, setSignedUrl] = useState<string | null>(null)

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'video':
        return 'bg-blue-100 text-blue-800'
      case 'quiz':
        return 'bg-purple-100 text-purple-800'
      case 'infographic':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-green-100 text-green-800'
    }
  }

  const getHardCodedDescription = (lesson: LearnContent) => {
    const title = lesson.title.toLowerCase()
    
    if (title.includes('account') && title.includes('opening')) {
      return "Learn the essential steps to open your first bank account like a professional. We'll cover required documents, choosing the right account type, and avoiding common mistakes."
    }
    
    if (title.includes('budgeting') || title.includes('budget')) {
      return "Master the fundamentals of personal budgeting. Discover proven techniques to track expenses, set financial goals, and build sustainable spending habits."
    }
    
    if (title.includes('saving') || title.includes('save')) {
      return "Explore effective saving strategies and learn how to build an emergency fund. Understand different types of savings accounts and maximize your money's growth potential."
    }
    
    if (title.includes('investment') || title.includes('investing')) {
      return "Get started with investment basics. Learn about different investment vehicles, risk management, and how to build a diversified portfolio for long-term wealth."
    }
    
    if (title.includes('credit') || title.includes('loan')) {
      return "Understand credit scores, how they work, and strategies to build excellent credit. Learn about different types of loans and responsible borrowing practices."
    }
    
    if (title.includes('insurance')) {
      return "Discover the importance of insurance in financial planning. Learn about different types of coverage and how to choose the right protection for your needs."
    }
    
    if (title.includes('tax') || title.includes('taxes')) {
      return "Navigate the basics of taxation and learn strategies to optimize your tax situation. Understand deductions, credits, and essential tax planning concepts."
    }
    
    if (title.includes('retirement')) {
      return "Plan for your financial future with retirement planning essentials. Learn about 401(k)s, IRAs, and strategies to secure your golden years."
    }
    
    if (title.includes('career') || title.includes('job')) {
      return "Develop your career and increase your earning potential. Learn negotiation skills, career advancement strategies, and professional development techniques."
    }
    
    if (title.includes('entrepreneur') || title.includes('business')) {
      return "Explore entrepreneurship and business fundamentals. Learn how to start a business, manage finances, and build sustainable income streams."
    }
    
    // Default descriptions based on content type
    switch (lesson.content_type) {
      case 'video':
        return "Watch this comprehensive video lesson to gain practical knowledge and actionable insights on this important financial topic."
      case 'quiz':
        return "Test your understanding with this interactive quiz. Challenge yourself and reinforce the key concepts you've learned."
      case 'infographic':
        return "Review this visual summary that breaks down complex concepts into easy-to-understand graphics and key points."
      default:
        return "Engage with this learning material to deepen your understanding and build practical financial skills."
    }
  }

  const handleStartContent = async () => {
    if (!currentLesson || !moduleId || !moduleTitle) return
    
    // Track module in recent learning when user starts content
    await addRecentModule({
      id: moduleId,
      title: moduleTitle,
      description: moduleDescription || '',
      category: moduleCategory || 'Wealth'
    })
    
    // For videos, completion is automatic when video is watched
    // No need to manually mark as completed here
  }

  // Create a signed URL for private bucket when we have a stored path
  useEffect(() => {
    const loadSignedUrl = async () => {
      // @ts-ignore - backend provides optional field
      const path = (currentLesson as any)?.video_file_path as string | undefined
      console.log('LessonPlayer - video_file_path:', path)
      
      if (path) {
        console.log('LessonPlayer - creating signed URL for path:', path)
        const { data, error } = await supabase.storage
          .from('module-videos')
          .createSignedUrl(path, 60 * 60) // 1 hour
        
        if (error) {
          console.error('LessonPlayer - signed URL error:', error)
        } else {
          console.log('LessonPlayer - signed URL created:', data?.signedUrl)
        }
        
        setSignedUrl(data?.signedUrl || null)
      } else {
        console.log('LessonPlayer - no video_file_path, setting signedUrl to null')
        setSignedUrl(null)
      }
    }
    loadSignedUrl()
  }, [currentLesson])

  const renderVideoPlayer = () => {
    // For private buckets, we only use signed URLs from video_file_path
    const videoSrc = signedUrl
    
    const handleVideoEnded = () => {
      if (currentLesson && !isContentCompleted(currentLesson.id)) {
        console.log('Video ended - marking content as completed')
        markContentAsCompleted(currentLesson.id)
      }
    }

    const handleVideoProgress = (e: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = e.currentTarget
      const progress = (video.currentTime / video.duration) * 100
      
      // Mark as completed if user watched 90% or more of the video
      if (progress >= 90 && currentLesson && !isContentCompleted(currentLesson.id)) {
        console.log('Video progress 90%+ - marking content as completed')
        markContentAsCompleted(currentLesson.id)
      }
    }
    
    return (
      <div className="bg-black rounded-lg aspect-video h-64 flex items-center justify-center mb-4 overflow-hidden">
        {videoSrc ? (
          <video 
            className="w-full h-full" 
            controls 
            playsInline 
            src={videoSrc}
            onEnded={handleVideoEnded}
            onTimeUpdate={handleVideoProgress}
          >
            Your browser does not support the video tag.
          </video>
        ) : (
          <div className="text-center text-white">
            <Play className="w-16 h-16 mx-auto mb-4 opacity-70" />
            <p className="text-lg font-medium">{currentLesson?.title}</p>
            <p className="text-sm opacity-70">Duration: {currentLesson?.duration}</p>
            <p className="text-xs opacity-50 mt-2">No video uploaded</p>
          </div>
        )}
      </div>
    )
  }

  const renderLessonContent = () => {
    if (!currentLesson) {
      return (
        <div className="flex-1 flex items-center justify-center bg-gray-100 rounded-lg h-64">
          <div className="text-center text-gray-500">
            <Play className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Select a lesson to begin</p>
          </div>
        </div>
      )
    }

    switch (currentLesson.content_type) {
      case 'video':
        return renderVideoPlayer()
      case 'quiz':
        return (
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-8 text-center mb-4 h-64 flex flex-col justify-center">
            <Award className="w-16 h-16 mx-auto mb-4 text-purple-600" />
            <p className="text-lg font-medium text-gray-900">{currentLesson.title}</p>
            <p className="text-sm text-gray-600">Interactive Quiz • {currentLesson.duration}</p>
          </div>
        )
      case 'infographic':
        return (
          <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-8 text-center mb-4 h-64 flex flex-col justify-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-orange-600" />
            <p className="text-lg font-medium text-gray-900">{currentLesson.title}</p>
            <p className="text-sm text-gray-600">Infographic • {currentLesson.duration}</p>
            {currentLesson.infographic_url && (
              <p className="text-xs text-gray-500 mt-2">Image: {currentLesson.infographic_url}</p>
            )}
          </div>
        )
      default:
        return (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-8 text-center mb-4 h-64 flex flex-col justify-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
            <p className="text-lg font-medium text-gray-900">{currentLesson.title}</p>
            <p className="text-sm text-gray-600">Reading Material • {currentLesson.duration}</p>
            {currentLesson.content_text && (
              <div className="mt-4 text-left bg-white p-4 rounded text-sm text-gray-700 max-h-32 overflow-y-auto">
                {currentLesson.content_text}
              </div>
            )}
          </div>
        )
    }
  }

  const isCompleted = currentLesson ? isContentCompleted(currentLesson.id) : false

  console.log('LessonPlayer - current lesson:', currentLesson?.id, 'isCompleted:', isCompleted)

  return (
    <Card className="bg-white rounded-xl shadow-sm border border-gray-100 h-full flex flex-col">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          {currentLesson && (
            <>
              <Badge variant="outline" className={getTypeColor(currentLesson.content_type)}>
                {currentLesson.content_type}
              </Badge>
              <h2 className="text-xl font-semibold text-gray-900">{currentLesson.title}</h2>
              {isCompleted && (
                <Badge className="bg-green-100 text-green-800">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Completed
                </Badge>
              )}
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        {renderLessonContent()}
        
        {/* Description Section */}
        {currentLesson && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Description</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {getHardCodedDescription(currentLesson)}
            </p>
          </div>
        )}
        
                {currentLesson && (
          <div className="flex gap-3 mt-auto">
            {isCompleted ? (
              <Button variant="outline" className="w-full">
                <CheckCircle className="w-4 h-4 mr-2" />
                Completed
              </Button>
            ) : (
              <div className="w-full text-center text-sm text-gray-500">
                Watch the video to mark as complete
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default LessonPlayer
